import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { finalize, map } from 'rxjs';
import { Antecipacao } from '../../../../core/models/parametros/beneficios.model';
import { BeneficiosService } from '../../../../core/services/parametros/beneficios.service';
import { ToastService } from '../../../../core/services/toast.service';
import { descreverAntecipacao } from '../../../../core/utils/parametros/beneficios.defaults';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestSwitchValueAccessorDirective } from '../../../../shared/directives/switch-vivest/vivest-switch-value-accessor.directive';
import { LoaderService } from '../../../../shared/services/loader/loader.service';

type SaldoAntecipacao = Antecipacao['saldo'];

export const PERCENTUAL_MAXIMO_ANTECIPACAO_SALDO = 25;

const percentualOk = (valor: number | null, maximo = 100) =>
  valor !== null && valor > 0 && valor <= maximo && /^\d+(\.\d{1,2})?$/.test(String(valor));

/** Erro da antecipação de saldo; com ela desligada, os percentuais não são validados. */
function erroSaldo({ permitir, percentualMinimo, percentualMaximo }: SaldoAntecipacao): string {
  if (!permitir) {
    return '';
  }
  if (
    !percentualOk(percentualMinimo, PERCENTUAL_MAXIMO_ANTECIPACAO_SALDO) ||
    !percentualOk(percentualMaximo, PERCENTUAL_MAXIMO_ANTECIPACAO_SALDO)
  ) {
    return `Informe os percentuais de 0,01 a ${PERCENTUAL_MAXIMO_ANTECIPACAO_SALDO}, com até duas casas decimais.`;
  }
  return percentualMinimo! > percentualMaximo! ? 'O percentual mínimo não pode ser maior que o máximo.' : '';
}

/** Com o adiantamento ligado, o percentual é obrigatório. */
function erroAdiantamento({ elegivel, percentual }: Antecipacao['adiantamentoBeneficio']): string {
  return elegivel && !percentualOk(percentual) ? 'Informe o percentual de 0,01 a 100, com até duas casas decimais.' : '';
}

function adiantamentoValido(grupo: AbstractControl): ValidationErrors | null {
  return erroAdiantamento(grupo.value as Antecipacao['adiantamentoBeneficio']) ? { adiantamentoInvalido: true } : null;
}

function saldoValido(grupo: AbstractControl): ValidationErrors | null {
  return erroSaldo(grupo.value as SaldoAntecipacao) ? { saldoInvalido: true } : null;
}

/** Seção "Antecipação": antecipação de saldo e adiantamento de benefício, para todo o plano. */
@Component({
  selector: 'app-beneficios-antecipacao',
  standalone: true,
  imports: [ReactiveFormsModule, VivestSwitchValueAccessorDirective, VivestModalAreaVisivelDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './beneficios-antecipacao.component.html',
  styleUrl: './beneficios-antecipacao.component.scss',
})
export class BeneficiosAntecipacaoComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly beneficiosService = inject(BeneficiosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly planoId = input.required<number>();
  readonly nomePlano = input('');

  /** Último estado salvo, para o Cancelar e para saber se há alterações. */
  private readonly carregado = signal<Antecipacao | null>(null);
  readonly carregou = computed(() => this.carregado() !== null);

  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);
  readonly tentouSalvar = signal(false);

  readonly form = this.fb.group({
    saldo: this.fb.group(
      {
        permitir: false,
        percentualMinimo: this.fb.control<number | null>(null),
        percentualMaximo: this.fb.control<number | null>(null),
      },
      { validators: [saldoValido] },
    ),
    adiantamentoBeneficio: this.fb.group(
      { elegivel: false, percentual: this.fb.control<number | null>(null) },
      { validators: [adiantamentoValido] },
    ),
  });

  readonly valor = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue() as Antecipacao)), {
    initialValue: this.form.getRawValue() as Antecipacao,
  });

  readonly temAlteracoes = computed(() => {
    const carregado = this.carregado();
    return !!carregado && JSON.stringify(this.valor()) !== JSON.stringify(carregado) && !this.salvando();
  });

  readonly invalido = computed(
    () => !!erroSaldo(this.valor().saldo) || !!erroAdiantamento(this.valor().adiantamentoBeneficio),
  );
  readonly erroPercentuais = computed(() => (this.tentouSalvar() ? erroSaldo(this.valor().saldo) : ''));
  readonly erroPercentualAdiantamento = computed(() =>
    this.tentouSalvar() ? erroAdiantamento(this.valor().adiantamentoBeneficio) : '',
  );
  readonly resumo = computed(() => descreverAntecipacao(this.valor()));

  ngOnInit(): void {
    this.loaderService.showLoader();
    this.beneficiosService
      .obterAntecipacao(this.planoId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (antecipacao) => {
          this.aplicar(antecipacao);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a antecipação.', 'error');
        },
      });
  }

  salvar(): void {
    this.tentouSalvar.set(true);
    if (!this.temAlteracoes()) {
      return;
    }
    if (this.invalido()) {
      this.toastService.show('Revise a antecipação antes de salvar.', 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.invalido() || this.salvando()) {
      return;
    }
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .salvarAntecipacao(this.planoId(), this.form.getRawValue() as Antecipacao)
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salva) => {
          this.aplicar(salva);
          this.toastService.show('Antecipação do plano salva.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar a antecipação.', 'error'),
      });
  }

  descartar(): void {
    const carregado = this.carregado();
    if (carregado) {
      this.aplicar(carregado);
    }
  }

  private aplicar(antecipacao: Antecipacao): void {
    this.form.reset(structuredClone(antecipacao));
    this.carregado.set(structuredClone(antecipacao));
    this.tentouSalvar.set(false);
  }
}
